import m from 'mithril';

export interface PagerAttrs {
    /** Zero-based */
    page: number;
    pageSize: number;
    /** Rows on the current page */
    rows: number;
    total: number;
    /** How many of the total the pages can show */
    reachable: number;
    onPage: (page: number) => void;
    labels: {previous: string; next: string; showing: string; windowLimit: string};
}

const NEIGHBOURS = 2;

export function pageCount(reachable: number, pageSize: number): number {
    return Math.max(1, Math.ceil(reachable / pageSize));
}

/** Page numbers to offer: the first, the last and the neighbours of the current one; null is a gap */
export function pageNumbers(page: number, count: number): (number | null)[] {
    const wanted = new Set([0, count - 1, ...Array.from({length: 2 * NEIGHBOURS + 1}, (_, i) => page - NEIGHBOURS + i)]);
    const sorted = Array.from(wanted).filter(number => number >= 0 && number < count).sort((a, b) => a - b);

    return sorted.flatMap((number, position) => position > 0 && number - sorted[position - 1] > 1 ? [null, number] : [number]);
}

/** The visible range, e.g. "11–20 / 345"; without rows there is nothing to range over */
export function showingText(attrs: Pick<PagerAttrs, 'page' | 'pageSize' | 'rows' | 'total' | 'labels'>): string {
    const from = attrs.rows === 0 ? 0 : attrs.page * attrs.pageSize + 1;
    const to = attrs.page * attrs.pageSize + attrs.rows;

    return attrs.labels.showing.replace('{0}', String(from)).replace('{1}', String(to)).replace('{2}', String(attrs.total));
}

/** Range text stays in print and the PNG (which page it is); the buttons do not */
export function pager(attrs: PagerAttrs): m.Children {
    const count = pageCount(attrs.reachable, attrs.pageSize);
    // Same buttons as the datagrid paginator (data_grid_paginator.latte), so all tables page alike
    const item = (content: m.Children, page: number, extra: {active?: boolean; disabled?: boolean; label?: string} = {}) => m('button.btn.btn-sm.btn-outline-primary', {
        type: 'button',
        class: extra.active ? 'active' : '',
        disabled: extra.disabled,
        title: extra.label,
        'aria-label': extra.label,
        'aria-current': extra.active ? 'page' : undefined,
        onclick: () => attrs.onPage(page),
    }, content);

    return m('.db-v2-answers-pager.d-flex.flex-wrap.align-items-center.justify-content-between.gap-2.px-2.py-2', [
        m('.text-body-secondary.fs-8', [
            m('span', showingText(attrs)),
            attrs.total > attrs.reachable && m('span.d-block', attrs.labels.windowLimit.replace('{0}', String(attrs.reachable)).replace('{1}', String(attrs.total))),
        ]),
        count > 1 && m('nav.d-flex.align-items-center.gap-2.d-print-none', {'data-html2canvas-ignore': ''}, [
            item(m('i.bi.bi-chevron-left', {'aria-hidden': 'true'}), attrs.page - 1, {disabled: attrs.page === 0, label: attrs.labels.previous}),
            // Unkeyed throughout: Mithril does not mix keyed and unkeyed siblings
            pageNumbers(attrs.page, count).map(number => number === null
                ? m('span', '…')
                : item(String(number + 1), number, {active: number === attrs.page})),
            item(m('i.bi.bi-chevron-right', {'aria-hidden': 'true'}), attrs.page + 1, {disabled: attrs.page >= count - 1, label: attrs.labels.next}),
        ]),
    ]);
}
